import { useEffect, useState } from 'react'
import { App } from '@capacitor/app'
import { Device } from '@capacitor/device'

export interface DeviceMeta {
  appVersion?: string
  deviceModel?: string
  osName?: string
  osVersion?: string
}

const MAX_FIELD_LENGTH = 120

const truncate = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  return value.length > MAX_FIELD_LENGTH ? value.slice(0, MAX_FIELD_LENGTH) : value
}

const normalizeOsName = (operatingSystem: string | undefined, platform: string): string => {
  const source = operatingSystem || platform || ''
  switch (source.toLowerCase()) {
    case 'ios':
      return 'iOS'
    case 'android':
      return 'Android'
    case 'mac':
      return 'macOS'
    case 'windows':
      return 'Windows'
    case 'web':
      return 'Web'
    default:
      return source || 'unknown'
  }
}

export const useDeviceMeta = () => {
  const [meta, setMeta] = useState<DeviceMeta>({})
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    const collect = async () => {
      try {
        const info = await Device.getInfo()
        let appVersion: string | undefined
        if (info.platform !== 'web') {
          try {
            const app = await App.getInfo()
            appVersion = app.version
          } catch {
            appVersion = undefined
          }
        }

        if (cancelled) return

        setMeta({
          appVersion: truncate(appVersion),
          deviceModel: truncate(info.model),
          osName: truncate(normalizeOsName(info.operatingSystem, info.platform)),
          osVersion: truncate(info.osVersion)
        })
      } catch {
        if (cancelled) return
        setMeta({})
      } finally {
        if (!cancelled) {
          setIsReady(true)
        }
      }
    }

    collect()

    return () => {
      cancelled = true
    }
  }, [])

  return { deviceMeta: meta, isDeviceMetaReady: isReady }
}
